import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Alert } from 'reactstrap';

import StoreForm from './StoreForm';
import XMCrud from '../../components/XMCrud';
import XMModalLog from '../../components/XMModalLog';
import XMModalConfirm from '../../components/XMModalConfirm';
import XMTable from '../../components/XMTable';
import XMDock from '../../components/XMDock';
import * as storeActions from '../../actions/storeActions';
import * as erpTypeActions from '../../actions/erpTypeActions';
import * as versionActions from '../../actions/versionActions';
import * as synchronisationActions from '../../actions/synchronisationActions';
import StoreStatusDropdown from './components/StoreStatusDropdown';
import StoreDeletionModal from './components/StoreDeletionModal';
import StoreTableRow from './components/StoreTableRow';
import StoreDetails from './components/StoreDetails';
import StoreDetailsRow from './components/StoreDetailsRow';
import filterStatuses from './filterStatuses';
import { stateFromQuery, queryFromState } from './listQuery';
import { isDevelopment } from '../../utils/environment';
import withRouter from '../../withRouter';

const Headers = [
  {},
  { name: 'Store', code: 'name', sortable: true },
  { name: 'DB', code: 'databaseSize', sortable: true, style: { minWidth: 80 } },
  { name: 'CDN', code: 'cdnSize', sortable: true, style: { minWidth: 80 } },
  { name: 'Contact', code: 'contactEmail', sortable: true, style: { minWidth: 100 } },
  { name: 'ERP', code: 'erpTypeVersion', sortable: true, style: { minWidth: 80 } },
  { name: 'Type', code: 'storeType', sortable: true, style: { minWidth: 80 } },
  // PDECOM-6293: sorts on the store's CURRENT status (Store.status), not the storeStatuses history
  // collection, which cannot be ordered by.
  { name: 'Status', code: 'status.statusType', sortable: true },
  { name: 'Actions', style: { width: 280 } },
];

const SortCodes = Headers.filter((h) => h.sortable).map((h) => h.code);

@connect((state) => {
  return {
    user: state.user,
    config: state.config,
    version: state.version,
    dataCentre: state.dataCentre,
    store: state.store,
    operator: state.operator,
    syncStoresStatus: state.sync.status.syncStoresStatus,
  };
})
class Stores extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: null,
      logger: false,
      confirmModal: false,
      deleteModal: false,
      // search, statusType ('' meaning every status, PDECOM-6293) and pagination come from the URL query
      ...stateFromQuery(props.router.location.search, SortCodes),
      // bumped when the URL changes from outside, to remount the uncontrolled search box and sort headers
      urlKey: 0,
      expandId: null,
      dock: false,
    };

    this.btns = [
      {
        icon: 'icon-chart',
        action: this.handleStats,
        tooltip: 'Stats',
      },
      {
        icon: 'icon-cloud-download',
        action: this.handleBackup,
        tooltip: 'Backup',
      },
      {
        icon: 'icon-note',
        action: this.handleEdit,
        tooltip: 'Edit',
      },
      {
        icon: 'icon-loop',
        action: this.handleSync,
        tooltip: 'Sync',
        isSync: true,
      },
    ];

    // Role-gated buttons (Client Credentials, Delete) are added/removed reactively
    // in componentDidUpdate so they stay in sync if user.selected loads after the
    // constructor runs, or changes while the page is mounted.
  }

  componentDidMount() {
    const { dispatch, dataCentre } = this.props;

    if (!_.isEmpty(dataCentre.selected)) {
      this.loadStores();
      this.loadStoreTypeCount();
    }

    dispatch(versionActions.getAll());
  }

  /**
   * Check if there is any "store creating" when the component is updated,
   * if so re-load till the status changes
   *
   * @param {*} prevProps
   */
  componentDidUpdate(prevProps) {
    const {
      store: { all },
      user,
    } = this.props;

    const isPartner = user.selected && user.selected.role === 'PARTNER';
    const hasIcon = (icon) => this.btns.some((b) => b.icon === icon);

    // Client Credentials: visible to non-Partners only.
    if (!isPartner && !hasIcon('icon-lock')) {
      this.btns.push({
        icon: 'icon-lock',
        action: this.handleClientCredentials,
        tooltip: 'Client Credentials',
      });
    } else if (isPartner && hasIcon('icon-lock')) {
      this.btns = this.btns.filter((b) => b.icon !== 'icon-lock');
    }

    // Delete: visible to super admins only.
    if (user.isSuperAdmin && !hasIcon('icon-trash')) {
      this.btns.push({
        icon: 'icon-trash',
        action: this.toggleConfirmModal,
        tooltip: 'Delete',
      });
    }

    // API Gateways: always visible (insert before the last existing button).
    if (!hasIcon('icon-puzzle')) {
      this.btns.splice(-1, 0, {
        icon: 'icon-puzzle',
        action: this.handleGateway,
        tooltip: 'API Gateways',
      });
    }

    // data centre selected: reload with the current filters. On a switch (not Root's select() on a fresh
    // page load, which must keep a linked ?page=) go back to the first page, which is the only one sure to exist.
    const { dataCentre } = this.props;
    if (dataCentre.selected !== prevProps.dataCentre.selected && !_.isEmpty(dataCentre.selected)) {
      if (_.isEmpty(prevProps.dataCentre.selected)) {
        this.loadStores();
      } else {
        this.setState((state) => ({ pagination: { ...state.pagination, page: undefined } }), this.reloadStores);
      }
      this.loadStoreTypeCount();
    }

    // URL changed from outside (e.g. the sidebar Stores link), not by syncUrl: re-read it
    const { search } = this.props.router.location;
    if (search !== prevProps.router.location.search && search !== queryFromState(this.state)) {
      this.setState((state) => ({ ...stateFromQuery(search, SortCodes), urlKey: state.urlKey + 1 }), this.loadStores);
    }

    if (all !== prevProps.store.all) {
      // any store with CREATING status?
      let storeCreating = _.find(all, (s) => {
        return this.isWaiting(s.storeStatus);
      });

      // TODO: move the "update time" (10s) to a Settings property?
      if (storeCreating && !this.timerId) {
        this.timerId = _.delay(this.loadStores, 10000);
      }

      // check sync2 store status
      this.loadStoresSync2Status();
    }
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * The search request the list is narrowed by: the fuzzy string from the search box and the
   * current-status filter, whichever are set. Empty values are left out entirely rather than sent
   * blank, so the API sees only what the operator actually asked for. PDECOM-6293.
   */
  searchRequest = () => {
    const { search, statusType } = this.state;
    const request = {};
    if (search && search.fuzzyString) {
      request.fuzzyString = search.fuzzyString;
    }
    if (statusType) {
      request.statusType = statusType;
    }
    return _.isEmpty(request) ? null : request;
  };

  syncUrl = () => {
    this.props.router.navigate({ search: queryFromState(this.state) }, { replace: true });
  };

  // for list-narrowing changes: record them in the URL, then reload
  reloadStores = () => {
    this.syncUrl();
    this.loadStores();
  };

  loadStores = () => {
    this.clearTimer();
    const { dispatch, dataCentre } = this.props;
    dispatch(
      storeActions.getAllByDataCentre(
        dataCentre.selected,
        this.state.pagination.page,
        this.state.pagination.size,
        this.state.pagination.sort,
        this.state.pagination.sortDir,
        this.searchRequest()
      )
    );
  };

  loadStoresSync2Status = () => {
    const { dispatch, store } = this.props;
    const storeIds = [];
    if (store && store.all && store.all.length) {
      store.all.map((s) => (this.isSync2(s) ? storeIds.push(s.storeId) : null));
    }
    if (storeIds.length) {
      dispatch(synchronisationActions.fetchStoresStatus(storeIds));
    }
  };

  loadStoreTypeCount = () => {
    const { dispatch, dataCentre } = this.props;
    dispatch(storeActions.getStoreTypeCount(dataCentre.selected));
  };

  /**
   * Waiting actions, eg: creating, deleting, etc.
   * @param {*} store
   */
  isWaiting = (storeStatus) => {
    return storeStatus?.statusType ? storeStatus.statusType.endsWith('ING') : false;
  };

  isSync2 = (store) => {
    return store.syncVersion === 'SYNC_2';
  };

  handlePagination = (page) => {
    this.setState(
      (state) => ({
        ...state,
        pagination: {
          ...state.pagination,
          page: page,
        },
      }),
      this.reloadStores
    );
  };

  handleSearch = (search) => {
    this.setState({ pagination: {}, search: search }, this.reloadStores);
  };

  handleStatusFilter = (event) => {
    this.setState({ pagination: {}, statusType: event.target.value }, this.reloadStores);
  };

  handleSort = (sort, sortDir) => {
    this.setState(
      (state) => ({
        ...state,
        pagination: {
          ...state.pagination,
          sort: sort,
          sortDir: sortDir,
        },
      }),
      this.reloadStores
    );
  };

  handleStats = (storeId) => {
    this.props.router.navigate(`/stores/${storeId}/stats`);
  };

  handleBackup = (storeId) => {
    const { dispatch } = this.props;
    dispatch(storeActions.getOne(storeId)).then(() => this.props.router.navigate(`/stores/${storeId}/backup`));
  };

  handleSync = (storeId) => {
    const { dispatch } = this.props;
    dispatch(storeActions.getOne(storeId)).then(() => this.props.router.navigate(`/stores/${storeId}/synchronization`));
  };

  handleClientCredentials = (storeId) => {
    const { dispatch } = this.props;
    dispatch(storeActions.getOne(storeId)).then(() => this.props.router.navigate(`/stores/${storeId}/credentials`));
  };

  handleGateway = (storeId) => {
    const { dispatch } = this.props;
    dispatch(storeActions.getOne(storeId)).then(() => this.props.router.navigate(`/stores/${storeId}/gateway`));
  };

  updateStoreStatus = (store, statusType, callback) => {
    if (typeof callback === 'undefined') {
      callback = () => {};
    }
    const { dispatch } = this.props;
    const storeStatus = { statusType: statusType };
    dispatch(storeActions.updateStoreStatus(store, storeStatus)).then(callback);
  };

  // CRUD Actions
  onCreate = () => {
    const { dispatch } = this.props;
    dispatch(erpTypeActions.getAll());
  };

  handleEdit = (storeId) => {
    const { dispatch } = this.props;
    // XMDock slides rather than unmounts, so reopening the SAME store leaves
    // StoreForm mounted with initialValues — and therefore its init effect —
    // unchanged. Clear here, where opening actually happens, or a failed save's
    // error greets the next open. PDECOM-5970 AC4.
    dispatch(storeActions.clearError());
    dispatch(storeActions.getOne(storeId));
    this.setState({ dock: true });
  };

  onEdit = (storeId) => {
    const { dispatch } = this.props;
    dispatch(storeActions.getOne(storeId));
    dispatch(erpTypeActions.getAll());
    this.setState({ dock: false });
  };

  onDelete = () => {
    const { store } = this.props;
    const deletingStore = store.all.find((s) => s.id === this.state.selected);
    this.updateStoreStatus(deletingStore, 'DELETE', () => {
      this.setState({ confirmModal: false });
    });
  };

  toggleConfirmModal = (storeId) => {
    this.setState((state) => ({ ...state, selected: storeId, confirmModal: !state.confirmModal }));
  };

  onDeleteConfirm = () => {
    const {
      user: { isSuperAdmin },
    } = this.props;

    this.setState(isSuperAdmin ? this.onDelete : this.toggleDeleteModal);
  };

  onCancel = () => {
    const { dispatch } = this.props;
    dispatch(storeActions.clear());
    this.setState({ dock: false });
  };

  logger = (store) => {
    const { dispatch } = this.props;
    if (this.state.logger && this.isWaiting(store.storeStatus)) {
      _.delay(() => {
        dispatch(storeActions.getOne(store.id))
          .then(() => this.logger(store))
          .catch(this.hideLog);
      }, 1000);
    }
  };

  showLog = (store, force = false) => {
    const { dispatch } = this.props;
    if (force || this.isWaiting(store.storeStatus)) {
      dispatch(storeActions.getOne(store.id));
      this.setState({ logger: true }, () => this.logger(store));
    }
  };

  hideLog = () => {
    const { dispatch } = this.props;
    dispatch(storeActions.clear());
    this.setState({ logger: false });
  };

  toggleDeleteModal = () => {
    this.setState((state) => ({ ...state, deleteModal: !state.deleteModal }));
  };

  getStoreName = () => {
    const { selected } = this.state;
    const {
      store: { all },
    } = this.props;

    const selectedStore = all.find((s) => s.id == selected);
    if (selectedStore) {
      sessionStorage.setItem('storePrimaryDomain', selectedStore.primaryDomain);
    }
    return selectedStore && selectedStore.name;
  };

  toggleExpand = (id) => {
    return () => {
      if (this.state.expandId && this.state.expandId === id) {
        this.setState({ expandId: null });
      } else {
        this.setState({ expandId: id });
      }
    };
  };

  render() {
    const { store, version, syncStoresStatus, config } = this.props;
    const statusTypes = filterStatuses((config && config.enums && config.enums.storeStatusType) || []);

    return (
      <React.Fragment>
        <XMModalLog
          title={'Store: ' + (store.one?.name ?? '')}
          isOpen={this.state.logger}
          isWaiting={this.isWaiting(store.one?.storeStatus)}
          toogle={this.hideLog}
          logs={store.one?.logs}
        />

        {store.one && (
          <XMDock title={store.one.name} isVisible={this.state.dock} handleCancel={this.onCancel}>
            <StoreForm
              initialValues={{ ...store.one, originName: store.one.name }}
              isEdit={true}
              handleCancel={this.onCancel}
              toggle={this.onCancel}
            />
          </XMDock>
        )}

        {store.provisionError && (
          <Alert color="warning" toggle={() => this.props.dispatch(storeActions.clearError())}>
            Store {store.provisionError.storeId} was created, but a step after it did not finish:{' '}
            {store.provisionError.message}. The store exists — do not create it again; open it to retry
            the remaining setup.
          </Alert>
        )}

        <XMCrud
          form={<StoreForm initialValues={{ ...store.one, originName: store.one.name }} />}
          details={<StoreDetails store={store} version={version} />}
          title={_.isEmpty(store.one) ? 'Create Store' : store.one.name}
          loading={store.loading}
          disableDelete={!this.props.user.isSuperAdmin}
          handleCreate={this.onCreate}
          handleEdit={this.onEdit}
          handleDelete={this.toggleConfirmModal}
          handleCancel={this.onCancel}
          handleSearch={this.handleSearch}
          searchValue={this.state.search?.fuzzyString}
          searchKey={this.state.urlKey}
          filters={
            <select
              className="form-control"
              name="statusType"
              aria-label="Filter by status"
              value={this.state.statusType}
              onChange={this.handleStatusFilter}
            >
              <option value="">All statuses</option>
              {statusTypes.map((statusType) => (
                <option key={statusType} value={statusType}>
                  {statusType}
                </option>
              ))}
            </select>
          }
          handlePagination={this.handlePagination}
          handleRefresh={this.loadStores}
          pagination={store.pagination}
          render={() => (
            <XMTable
              key={this.state.urlKey}
              headers={Headers}
              autoRenderRow={false}
              handleSort={this.handleSort}
              sort={this.state.pagination.sort}
              sortDir={this.state.pagination.sortDir}
            >
              {store.all.map((s) => [
                <tr key={s.id}>
                  <StoreTableRow
                    store={s}
                    handleEdit={this.handleEdit}
                    onStoreClick={this.toggleExpand(s.id)}
                    btns={this.btns}
                    expand={this.state.expandId === s.id}
                    syncStatus={syncStoresStatus[s.storeId] ? syncStoresStatus[s.storeId] : ''}
                    dropdownComponent={
                      <StoreStatusDropdown
                        store={s}
                        onToggleClick={() => this.showLog(s)}
                        onFailedClick={() => this.showLog(s, true)}
                        onUpdateStatusClick={(status) => this.updateStoreStatus(s, status)}
                      />
                    }
                  />
                </tr>,
                <StoreDetailsRow key={`${s.id}-expand`} {...s} expand={this.state.expandId === s.id} />,
              ])}
            </XMTable>
          )}
        ></XMCrud>

        <XMModalConfirm
          message={`Are you sure you want to delete store ${this.getStoreName()}?`}
          isVisible={this.state.confirmModal}
          onCancel={this.toggleConfirmModal}
          handleConfirm={this.onDeleteConfirm}
          isValidatingStoreUrl={!isDevelopment()}
        />

        <StoreDeletionModal
          isOpen={this.state.deleteModal}
          onClose={this.toggleDeleteModal}
          onCancel={this.onCancel}
          onConfirm={this.onDelete}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(Stores);
